import { makeChangeEvent } from '@findigs/helpers/src/helpers/make-change-event';
import { useCallback } from 'react';

export type UseInputTrimOnBlurProps<E extends { value: string }> = {
  /** set to false to disable trimming */
  enabled: boolean;
  /**
   * can be used to forward the blur event
   *
   * if your component exposes a blur event, you should pass it here
   */
  forwardedOnBlur?: (event: React.FocusEvent<E, Element>) => void;
  /**
   * can be used to forward the change event
   *
   * if your component exposes a change event, you should pass it here
   */
  onChange?: (event: React.ChangeEvent<E>) => void;
};

/**
 * Hook that implements input trimming on blur
 */
export function useInputTrimOnBlur<E extends { value: string }>({
  enabled,
  forwardedOnBlur,
  onChange,
}: UseInputTrimOnBlurProps<E>) {
  const onBlur = useCallback(
    (e: React.FocusEvent<E, Element>) => {
      if (enabled) {
        const v1 = e.target.value;
        const v2 = v1.trim();

        if (v1 !== v2) {
          e.target.value = v2;

          if (onChange) {
            onChange(makeChangeEvent(e.target));
          }
        }
      }

      if (forwardedOnBlur) {
        forwardedOnBlur(e);
      }
    },
    [forwardedOnBlur, enabled, onChange]
  );

  return { onBlur };
}
